/*
 * Assistance entry points → the Chatwoot widget. The default Chatwoot bubble is
 * hidden (see snippets/chatwoot-embed.liquid), so the widget is opened from:
 *   - the header "Assistance" trigger, the mobile-menu item, and the mobile
 *     floating button ([data-assistance-trigger]); each carries a
 *     [data-assistance-source] so opens are attributed per entry point (GA4 via
 *     Shopify.analytics.publish + a Chatwoot entry_source contact attribute)
 *   - any link that points at the contact page — it opens the widget instead of
 *     navigating, and falls back to the contact page itself when the widget is
 *     unavailable (Chatwoot not configured, or the SDK never loads).
 *
 * Unread badge: the widget posts its unread count to the host page as
 * `chatwoot-widget:{event:"handleNotificationDot",unreadMessageCount}`. We mirror
 * that count onto every [data-assistance-badge] (desktop trigger, mobile menu
 * item, mobile hamburger) so customers see unread replies without opening chat.
 */
(function () {
  const WOOT_PREFIX = "chatwoot-widget:";
  // How long to wait for the widget to load before falling back to navigation.
  const READY_TIMEOUT = 5000;
  const IDLE_TIMEOUT = 2000;
  let loaderState = "idle";
  let scheduledLoad = null;

  // Read live: the Liquid snippet always supplies the Contact fallback and adds
  // the Chatwoot credentials only when the widget is configured.
  function config() {
    return window.UMIAssistance || {};
  }

  function chatwootConfigured() {
    const settings = config();
    return !!(
      settings.enabled &&
      typeof settings.baseUrl === "string" &&
      settings.baseUrl &&
      typeof settings.websiteToken === "string" &&
      settings.websiteToken
    );
  }

  function assistanceEnabled() {
    return !!(chatwootConfigured() || window.$chatwoot);
  }

  function cancelScheduledLoad() {
    if (!scheduledLoad) return;
    if (
      scheduledLoad.kind === "idle" &&
      typeof window.cancelIdleCallback === "function"
    ) {
      window.cancelIdleCallback(scheduledLoad.handle);
    } else {
      window.clearTimeout(scheduledLoad.handle);
    }
    scheduledLoad = null;
  }

  function ensureChatwootLoaded() {
    cancelScheduledLoad();

    if (window.$chatwoot) {
      if (loaderState === "idle") loaderState = "loading";
      if (loaderState === "loading") loaderState = "ready";
      return true;
    }
    if (!chatwootConfigured()) return false;
    if (loaderState !== "idle") return loaderState !== "failed";

    loaderState = "loading";
    const settings = config();
    const script = document.createElement("script");
    const firstScript = document.getElementsByTagName("script")[0];
    script.src = settings.baseUrl + "/packs/js/sdk.js";
    script.defer = true;
    script.async = true;
    script.onload = () => {
      try {
        window.chatwootSDK.run({
          websiteToken: settings.websiteToken,
          baseUrl: settings.baseUrl,
        });
      } catch (error) {
        if (loaderState === "loading") loaderState = "failed";
      }
    };
    script.onerror = () => {
      if (loaderState === "loading") loaderState = "failed";
    };
    firstScript.parentNode.insertBefore(script, firstScript);
    return true;
  }

  function scheduleChatwootLoad() {
    if (!chatwootConfigured() || loaderState !== "idle" || scheduledLoad) {
      return;
    }

    const start = () => {
      scheduledLoad = null;
      ensureChatwootLoaded();
    };
    if (typeof window.requestIdleCallback === "function") {
      scheduledLoad = {
        kind: "idle",
        handle: window.requestIdleCallback(start, { timeout: IDLE_TIMEOUT }),
      };
    } else {
      scheduledLoad = {
        kind: "timeout",
        handle: window.setTimeout(start, 0),
      };
    }
  }

  // Report which entry point opened chat. GA4 lives only inside Shopify's
  // sandboxed web pixel, so we publish a custom storefront event; a custom pixel
  // (Settings → Customer events) subscribes and forwards it to GA4. No-op when
  // analytics isn't present, so tracking never blocks opening the widget.
  function trackOpen(source) {
    try {
      if (
        window.Shopify &&
        window.Shopify.analytics &&
        typeof window.Shopify.analytics.publish === "function"
      ) {
        window.Shopify.analytics.publish("umi:assistance_opened", {
          source: source || "unknown",
        });
      }
    } catch (error) {
      /* analytics must never break the widget */
    }
  }

  // Tag the contact with the doorway they used, so the same signal is visible
  // inside Chatwoot as well as GA4. Best-effort.
  function setEntrySource(source) {
    try {
      if (
        source &&
        window.$chatwoot &&
        typeof window.$chatwoot.setCustomAttributes === "function"
      ) {
        window.$chatwoot.setCustomAttributes({ entry_source: source });
      }
    } catch (error) {
      /* attribute is best-effort */
    }
  }

  // Open the widget. If it isn't ready yet, open it once it is; when an
  // onUnavailable callback is given, fall back to it if the widget never loads.
  function openChat(source, onUnavailable) {
    ensureChatwootLoaded();

    if (window.$chatwoot) {
      setEntrySource(source);
      window.$chatwoot.toggle("open");
      return;
    }

    let settled = false;
    const onReady = () => {
      if (settled) return;
      settled = true;
      if (window.$chatwoot) {
        setEntrySource(source);
        window.$chatwoot.toggle("open");
      }
    };
    window.addEventListener("chatwoot:ready", onReady, { once: true });

    if (typeof onUnavailable === "function") {
      window.setTimeout(() => {
        if (settled) return;
        settled = true;
        window.removeEventListener("chatwoot:ready", onReady);
        onUnavailable();
      }, READY_TIMEOUT);
    }
  }

  // When opened from the mobile menu, close that menu first so we don't stack
  // the menu drawer under the chat panel.
  function closeHeaderDrawer() {
    const headerDrawer = document.querySelector("header-drawer");
    if (!headerDrawer || typeof headerDrawer.closeMenuDrawer !== "function") {
      return;
    }
    const details = headerDrawer.querySelector("details");
    if (!details || !details.hasAttribute("open")) return;

    const summary = headerDrawer.querySelector("summary");
    if (summary) summary.setAttribute("aria-expanded", "false");
    headerDrawer.closeMenuDrawer(new Event("click"), summary);
  }

  function bindTriggers() {
    document
      .querySelectorAll("[data-assistance-trigger]")
      .forEach((trigger) => {
        trigger.addEventListener("click", (event) => {
          event.preventDefault();
          const source = trigger.dataset.assistanceSource || "unknown";
          trackOpen(source);
          closeHeaderDrawer();
          // Same fallback contract as contact links: no widget → contact page.
          if (!assistanceEnabled()) {
            goToContact();
            return;
          }
          openChat(source, goToContact);
        });
      });
  }

  // The contact page path (from Liquid), normalised without a trailing slash.
  function contactPath() {
    const raw = config().contactUrl;
    if (typeof raw !== "string" || raw === "") return null;
    try {
      return new URL(raw, window.location.origin).pathname.replace(/\/+$/, "");
    } catch (error) {
      return null;
    }
  }

  // Fall back to the contact page, unless it's unknown or we're already on it.
  function goToContact() {
    const raw = config().contactUrl;
    const target = contactPath();
    if (!raw || !target) return;
    if (window.location.pathname.replace(/\/+$/, "") === target) return;
    window.location.assign(raw);
  }

  function isContactLink(link) {
    let path;
    try {
      path = new URL(link.href, window.location.origin).pathname.replace(
        /\/+$/,
        "",
      );
    } catch (error) {
      return false;
    }
    const target = contactPath();
    if (target) return path === target;
    // Fallback when the contact URL wasn't supplied: match the default handle.
    return path.endsWith("/pages/contact");
  }

  // Delegate so links added after load (drawers, sections) are covered too.
  function bindContactLinks() {
    document.addEventListener("click", (event) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      // No widget on this page → let the link navigate to the contact page.
      if (!assistanceEnabled()) return;
      if (!(event.target instanceof Element)) return;

      const link = event.target.closest("a[href]");
      if (!link || link.target === "_blank") return;
      if (!isContactLink(link)) return;

      event.preventDefault();
      trackOpen("contact_link");
      closeHeaderDrawer();
      openChat("contact_link", () => window.location.assign(link.href));
    });
  }

  function updateBadges(rawCount) {
    const count = Number(rawCount) || 0;
    document.querySelectorAll("[data-assistance-badge]").forEach((badge) => {
      badge.textContent = count > 9 ? "9+" : String(count);
      badge.hidden = count === 0;
    });
  }

  function listenForUnread() {
    window.addEventListener("message", (event) => {
      if (
        typeof event.data !== "string" ||
        event.data.indexOf(WOOT_PREFIX) !== 0
      ) {
        return;
      }
      let payload;
      try {
        payload = JSON.parse(event.data.replace(WOOT_PREFIX, ""));
      } catch (error) {
        return;
      }
      if (payload && payload.event === "handleNotificationDot") {
        updateBadges(payload.unreadMessageCount);
      }
    });
  }

  // Give the widget a cart-drawer feel: a dimmed backdrop behind the panel that
  // also closes it on click. The panel geometry itself is CSS (chatwoot-embed).
  function setupDrawer() {
    if (!assistanceEnabled()) return;

    let backdrop;
    const ensureBackdrop = () => {
      if (backdrop) return backdrop;
      backdrop = document.createElement("div");
      backdrop.className = "umi-assistance-backdrop";
      backdrop.addEventListener("click", () => {
        if (window.$chatwoot) window.$chatwoot.toggle("close");
      });
      document.body.appendChild(backdrop);
      return backdrop;
    };

    // The widget is open whenever its holder lacks the SDK's `woot--hide` class.
    const wire = () => {
      const holder = document.querySelector(".woot-widget-holder");
      if (!holder) return false;
      const bd = ensureBackdrop();
      const sync = () => {
        const open = !holder.classList.contains("woot--hide");
        bd.classList.toggle("is-visible", open);
        // Match the menu/cart drawers: hide the storefront forms popup + chat
        // bubble while the assistance drawer is open.
        document.body.classList.toggle("umi-assistance-open", open);
      };
      new MutationObserver(sync).observe(holder, {
        attributes: true,
        attributeFilter: ["class"],
      });
      sync();
      return true;
    };

    if (wire()) return;
    // The holder is created after the SDK loads — retry briefly once it's ready.
    const onReady = () => {
      let tries = 0;
      const timer = window.setInterval(() => {
        if (wire() || (tries += 1) > 25) window.clearInterval(timer);
      }, 200);
    };
    if (window.$chatwoot) onReady();
    else window.addEventListener("chatwoot:ready", onReady, { once: true });
  }

  function init() {
    bindTriggers();
    bindContactLinks();
    listenForUnread();
    setupDrawer();
  }

  window.addEventListener(
    "chatwoot:ready",
    () => {
      if (loaderState === "loading") loaderState = "ready";
    },
    { once: true },
  );

  if (document.readyState === "complete") {
    scheduleChatwootLoad();
  } else {
    window.addEventListener("load", scheduleChatwootLoad, { once: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
